type DateInputFormat = 'DD/MM/YYYY' | 'MM/DD/YYYY' | 'YYYY/MM/DD' | 'YYYY';

type UserDateTimeOptions = Intl.DateTimeFormatOptions & {
  locale?: string;
  timeZone?: string;
};

type ExplicitDateTimeOptionKey =
  | 'weekday'
  | 'year'
  | 'month'
  | 'day'
  | 'hour'
  | 'minute'
  | 'second'
  | 'dayPeriod'
  | 'timeZoneName';

const USER_DATE_PARTS = new Set(['day', 'month', 'year']);
const ISO_DATETIME_WITHOUT_TIMEZONE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,6})?$/;
const EXPLICIT_DATE_TIME_OPTION_KEYS: ExplicitDateTimeOptionKey[] = [
  'weekday',
  'year',
  'month',
  'day',
  'hour',
  'minute',
  'second',
  'dayPeriod',
  'timeZoneName',
];

const hasExplicitDateTimeParts = (options: Intl.DateTimeFormatOptions) =>
  EXPLICIT_DATE_TIME_OPTION_KEYS.some((optionKey) => options[optionKey] != null);

export const getUserLocale = () => Intl.DateTimeFormat().resolvedOptions().locale;

export const getUserTimeZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone;

export const getLocalizedDateInputFormat = (locale: string = getUserLocale()): DateInputFormat => {
  const orderedParts = new Intl.DateTimeFormat(locale)
    .formatToParts(new Date(2006, 10, 22))
    .filter((part) => USER_DATE_PARTS.has(part.type))
    .map((part) => part.type);

  if (orderedParts[0] === 'year') {
    return 'YYYY/MM/DD';
  }

  if (orderedParts[0] === 'month') {
    return 'MM/DD/YYYY';
  }

  return 'DD/MM/YYYY';
};

const UPPERCASE_LETTER_REGEX = /[A-Z]/g;

export const getDateInputMask = (inputFormat: DateInputFormat): string =>
  inputFormat.replace(UPPERCASE_LETTER_REGEX, '_');

export const toDate = (value: string | number | Date | null | undefined) => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const normalizedValue = typeof value === 'string' && ISO_DATETIME_WITHOUT_TIMEZONE.test(value) ? `${value}Z` : value;

  const parsedDate = value instanceof Date ? value : new Date(normalizedValue);

  if (Number.isNaN(parsedDate.getTime())) {
    return null;
  }

  return parsedDate;
};

export const formatDateTimeForUser = (
  value: string | number | Date | null | undefined,
  options: UserDateTimeOptions = {}
) => {
  const parsedDate = toDate(value);

  if (!parsedDate) {
    return '';
  }

  const { locale = getUserLocale(), timeZone = getUserTimeZone(), ...formatOptions } = options;
  const shouldUseDefaultDateTimeStyle =
    !hasExplicitDateTimeParts(formatOptions) && formatOptions.dateStyle == null && formatOptions.timeStyle == null;
  const hasStyleOverride = formatOptions.dateStyle != null || formatOptions.timeStyle != null;

  return new Intl.DateTimeFormat(locale, {
    timeZone,
    ...(shouldUseDefaultDateTimeStyle && !hasStyleOverride ? { dateStyle: 'medium', timeStyle: 'short' } : {}),
    ...formatOptions,
  }).format(parsedDate);
};

export const formatDateForUser = (
  value: string | number | Date | null | undefined,
  options: UserDateTimeOptions = {}
) => {
  const { timeStyle, ...formatOptions } = options;
  const shouldUseDefaultDateStyle = formatOptions.dateStyle == null && !hasExplicitDateTimeParts(formatOptions);

  return formatDateTimeForUser(value, {
    ...(shouldUseDefaultDateStyle ? { dateStyle: 'medium' } : {}),
    ...formatOptions,
    ...(timeStyle ? { timeStyle } : {}),
  });
};

export const formatRelativeTimeForUser = (
  value: string | number | Date | null | undefined,
  {
    locale = getUserLocale(),
    now = new Date(),
  }: {
    locale?: string;
    now?: string | number | Date | null | undefined;
  } = {}
) => {
  const parsedDate = toDate(value);
  const parsedNow = toDate(now);

  if (!parsedDate || !parsedNow) {
    return '';
  }

  const diffMs = parsedDate.getTime() - parsedNow.getTime();
  const diffSeconds = Math.round(diffMs / 1000);
  const absDiffSeconds = Math.abs(diffSeconds);
  const relativeTimeFormatter = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });

  if (absDiffSeconds < 5) {
    return relativeTimeFormatter.format(0, 'second');
  }

  if (absDiffSeconds < 60) {
    return relativeTimeFormatter.format(diffSeconds, 'second');
  }

  const diffMinutes = Math.round(diffSeconds / 60);
  const absDiffMinutes = Math.abs(diffMinutes);
  if (absDiffMinutes < 60) {
    return relativeTimeFormatter.format(diffMinutes, 'minute');
  }

  const diffHours = Math.round(diffMinutes / 60);
  const absDiffHours = Math.abs(diffHours);
  if (absDiffHours < 24) {
    return relativeTimeFormatter.format(diffHours, 'hour');
  }

  const diffDays = Math.round(diffHours / 24);
  const absDiffDays = Math.abs(diffDays);
  if (absDiffDays < 7) {
    return relativeTimeFormatter.format(diffDays, 'day');
  }

  const diffWeeks = Math.round(diffDays / 7);
  const absDiffWeeks = Math.abs(diffWeeks);
  if (absDiffWeeks < 5) {
    return relativeTimeFormatter.format(diffWeeks, 'week');
  }

  const diffMonths = Math.round(diffDays / 30);
  const absDiffMonths = Math.abs(diffMonths);
  if (absDiffMonths < 12) {
    return relativeTimeFormatter.format(diffMonths, 'month');
  }

  const diffYears = Math.round(diffDays / 365);
  return relativeTimeFormatter.format(diffYears, 'year');
};
